import { useEffect, useRef, useState } from "react";

import { QueryClient, useQueryClient } from "react-query";

import {
  ListSourceTestStepsQuery,
  ListSourceTestStepsQueryVariables,
  StartTestSourceBackgroundJobQuery,
  TestSourceInput,
  TestSourceQuery,
  TestSourceResult,
  useListSourceTestStepsQuery,
  useStartTestSourceBackgroundJobQuery,
  useTestSourceBackgroundJobResultQuery,
} from "src/graphql";
import { backoffPollIntervalMs } from "src/utils/poll-backoff";
import { sleep } from "src/utils/promise";

export const useSourceTesting = (): {
  results: Partial<TestSourceResult> | undefined;
  steps: ListSourceTestStepsQuery["listSourceTestSteps"] | undefined;
  getTestSteps: (variables: ListSourceTestStepsQueryVariables) => Promise<void>;
  runTest: (input: TestSourceInput) => void;
  cancelRunTest: () => void;
  timeElapsed: number | undefined;
} => {
  const [testResult, setTestResult] = useState<
    TestSourceQuery["testSource"] | undefined
  >();
  const [delayedTestResult, setDelayedTestResult] = useState<
    Partial<TestSourceQuery["testSource"]> | undefined
  >();
  const [testSteps, setTestSteps] = useState<
    ListSourceTestStepsQuery["listSourceTestSteps"] | undefined
  >();
  const [counter, setCounter] = useState<number>();

  const [jobId, setJobId] = useState<string | undefined>();

  const client = useQueryClient();

  // Track poll attempt count for exponential backoff. Reset on new jobId.
  const pollAttemptRef = useRef(0);
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    pollAttemptRef.current = 0;
  }, [jobId]);

  useEffect(() => {
    const set = async (testResult) => {
      for (const step of testResult.stepResults) {
        await sleep(Math.random() * (1000 - 500) + 500);
        setDelayedTestResult((result) => ({
          stepResults: [...(result?.stepResults || []), step],
        }));
      }
      setDelayedTestResult((result) => ({
        ...result,
        success: testResult.success,
      }));
    };

    if (testResult) {
      setCounter(undefined);
      set(testResult);
    } else {
      setDelayedTestResult(testResult);
    }
  }, [testResult]);

  useEffect(() => {
    let timer;
    if (counter !== undefined) {
      timer = setTimeout(() => setCounter(counter + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [counter]);

  const getTestSteps = async (variables: ListSourceTestStepsQueryVariables) => {
    const queryKey = useListSourceTestStepsQuery.getKey(variables);
    const queryFn = useListSourceTestStepsQuery.fetcher(variables);

    const response = await client.fetchQuery<ListSourceTestStepsQuery>(
      queryKey,
      {
        queryFn,
        cacheTime: 0,
      },
    );

    setTestSteps(response?.listSourceTestSteps);
  };

  useTestSourceBackgroundJobResultQuery(
    {
      jobId: jobId!,
    },
    {
      enabled: Boolean(jobId),
      refetchInterval: () => {
        const ms = backoffPollIntervalMs(pollAttemptRef.current);
        pollAttemptRef.current += 1;
        return ms;
      },
      onSuccess(data) {
        switch (data.testSourceBackgroundJobResult.__typename) {
          case "TestSourceBackgroundJobProcessing":
            break;
          // TODO: should return error somewhere, but currently no consumers handle errors
          case "TestSourceBackgroundJobFailure": {
            setJobId(undefined);
            break;
          }
          case "TestSourceBackgroundJobSuccess": {
            setJobId(undefined);
            setTestResult(data.testSourceBackgroundJobResult.result);
            break;
          }
        }
      },
      onError() {
        // TODO: should return error somewhere, but currently no consumers handle errors
        setJobId(undefined);
      },
    },
  );

  const runTest = async (input: TestSourceInput) => {
    setTestResult(undefined);
    setDelayedTestResult(undefined);
    setCounter(0);

    const res = await startTestSourceBackgroundJob(input, client);

    // Setting job id starts query which polls for test results
    setJobId(res?.jobId);
  };

  // Clear the job id so we stop polling for results
  // Useful for callers eg. when a test modal is closed
  const cancelRunTest = async () => {
    setJobId(undefined);
  };

  return {
    results: delayedTestResult,
    steps: testSteps,
    getTestSteps,
    runTest,
    cancelRunTest,
    timeElapsed: counter,
  };
};

const startTestSourceBackgroundJob = async (
  input: TestSourceInput,
  client: QueryClient,
): Promise<{ jobId: string } | undefined> => {
  const queryKey = useStartTestSourceBackgroundJobQuery.getKey({ input });
  const queryFn = useStartTestSourceBackgroundJobQuery.fetcher({ input });

  const response = await client.fetchQuery<StartTestSourceBackgroundJobQuery>(
    queryKey,
    {
      queryFn,
      cacheTime: 0,
    },
  );

  return response?.startTestSourceBackgroundJob;
};
